import { createFileRoute } from '@tanstack/react-router';
import type { HistoryState } from '@tanstack/react-router';
import { type UpdatePinProps } from './-model/props';
import { useGetAccountPins } from '../(pins)/-hooks/pins.hooks';
import { useForm } from '@deadcow-enterprises/react-forms';
import z from 'zod';
import {
  logger,
  ModuleName,
  Screen,
  type SkinPageConfig,
  templates,
  widgets,
} from '@nosinovacao/nosid-mfe-common';
import { useEffect } from 'react';
import { UpdatePinLight, UpdatePinWooDark, UpdatePinWoo } from './-skins';
import { ErrorSidePanel } from '@/components/errors';

export const Route = createFileRoute('/(tv)/tv/$id/(pins)/_sheet/pins/$pin/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  staticData: {
    module: ModuleName.Tv,
    screen: Screen.Pins,
  },
});

function RouteComponent() {
  const { id, pin: pinType } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data, isLoadingData } = useGetAccountPins(id);
  const targetPin = data?.find((p) => p.Type === pinType);

  const form = useForm(
    {
      pin: {
        value: targetPin?.Code ?? '',
        validator: z.string().length(widgets.PinCodeLength, 'Pins.ChangePin.Errors.PinRequired'),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  useEffect(() => {
    if (form.value.pin === '' && form.pristine && !!targetPin?.Code) {
      form.reset({ pin: targetPin.Code });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPin?.Code]);

  const props: UpdatePinProps = {
    isLoading: isLoadingData,
    form,
    submit: async ({ pin }) => {
      const newPins =
        data?.map((p) => (p.Type === targetPin?.Type ? { ...p, Code: pin } : p)) ?? [];
      navigate({
        to: '/tv/$id/pins/$pin/contact-selection',
        params: { id, pin: pinType },
        state: { pins: newPins } as HistoryState,
      });
    },
  };
  return templates.getSkinSelector(getSkin(props));
}

function getSkin(props: UpdatePinProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <UpdatePinWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <UpdatePinWoo {...props} />,
      },
    ],
    defaultValue: <UpdatePinLight {...props} />,
  };
}
